import React from 'react';
import { useNavigate } from 'react-router-dom';
import '../../Styles/Legal/termsStyle.css';

function Terms() {
    const navigate = useNavigate();

    return (
        <div className="terms-container">
            <header className="terms-header">
                <h1>
                    BIG CIRCLE AUCTIONS LLC<br />
                    TERMS AND CONDITIONS AGREEMENT (BIDDER)
                </h1>
            </header>

            <section className="terms-content">
                <h2>TERMS AND CONDITIONS</h2>

                <p><strong>BIG CIRCLE AUCTIONS LLC<br />Effective Date: July 10, 2025</strong></p>

                <p>
                    These Terms and Conditions (“Terms”) set out the basis upon which users may access and engage with the
                    auction platform operated by Big Circle Auctions LLC (“BCA”, “we”, “us”, or “our”). BCA is a limited
                    liability company organized in the State of Michigan with its registered office at
                    414 S Main Street #201, Rochester, MI 48307.
                </p>

                <p>
                    These Terms govern all interactions between BCA and any person or legal entity (“you”, “user”,
                    “bidder”, or “participant”) who accesses, uses, or transacts via the BCA platform, including the
                    purchase of access services or submission of bids during any auction process.
                </p>

                <p>
                    By registering an account, paying a service fee, or using the BCA platform in any capacity, you
                    accept and agree to be legally bound by these Terms in full.
                </p>

                <h3>1. DEFINITIONS AND INTERPRETATION</h3>
                <p>1.1 In these Terms:</p>
                <ul>
                    <li>
                        (a) “Platform” means the digital technology environment operated by BCA, including all web
                        portals, interfaces, mobile applications, APIs, auction software, documentation, and related
                        systems provided for the purpose of enabling auction-based transactions.
                    </li>
                    <li>
                        (b) “Auction” refers to a time-limited sales event hosted via the Platform, in which users may
                        submit monetary offers for specified assets or items.
                    </li>
                    <li>
                        (c) “Service Fee” means a non-refundable payment required to advance in a round of an auction
                        until the minimum requirement has been met.
                    </li>
                    <li>
                        (d) “Best and Final Offer Stage” refers to the final bidding phase of a designated auction
                        event, accessible only to eligible participants who have paid the corresponding service fee.
                    </li>
                    <li>
                        (e) “Successful Bidder” means a participant whose offer is accepted as winning, subject to
                        BCA’s confirmation, legal finality, and fulfilment of payment terms.
                    </li>
                    <li>
                        (f) “Financed Offer” refers to a bid contingent on third-party financing, deferred
                        consideration, or conditional payment structures, in contrast to immediate cash offers.
                    </li>
                    <li>
                        (g) “Applicable Law” means all relevant laws, regulations, codes of conduct, and binding
                        guidance in the United States, and the State of Michigan and Oakland County, as applicable to
                        your use of the Platform and participation in auctions.
                    </li>
                </ul>

                <p>
                    1.2 Clause headings are for convenience only and shall not affect interpretation. Words denoting
                    the singular include the plural and vice versa. Any reference to a statutory provision shall be
                    deemed to include any re-enactment or modification thereof.
                </p>

                <h3>2. NATURE AND PURPOSE OF THE PLATFORM</h3>
                <p>
                    2.1 BCA operates a digital auction environment that facilitates competitive bidding on physical or
                    intangible assets offered by third parties or vendors.
                </p>
                <p>
                    2.2 The Platform enables eligible users to participate in auction events following payment of a
                    service fee. BCA is not a seller, agent, fiduciary, or party to any transaction concluded as a
                    result of auction activity.
                </p>
                <p>
                    2.3 All information provided on the Platform regarding auctioned assets, including descriptions,
                    valuations, photographs, legal title, or performance history, is supplied “as is”, and BCA does
                    not guarantee its accuracy or completeness. Due diligence remains the sole responsibility of the
                    bidder.
                </p>
                <p>
                    2.4 Access to the Platform constitutes a limited, conditional right to interact with BCA’s
                    services and infrastructure, and does not confer any ownership or proprietary interest in the
                    Platform itself or the items being auctioned.
                </p>

                <h3>3. ELIGIBILITY AND REGISTRATION</h3>
                <p>
                    3.1 To register for access to the Platform, users must be natural persons aged 18 or over, or
                    corporate entities represented by authorized individuals with legal capacity to bind the entity
                    contractually.
                </p>
                <p>
                    3.2 Users must provide accurate, complete, and up-to-date information during registration and must
                    not impersonate another individual or entity.
                </p>
                <p>
                    3.3 BCA reserves the right, at its sole discretion, to refuse or revoke registration, restrict
                    access, or suspend participation where it believes that a user may pose a legal, reputational, or
                    operational risk to BCA or other participants.
                </p>
                <p>
                    3.4 Your account is personal to you. Sharing access credentials, using the account on behalf of an
                    unauthorized party, or facilitating third-party entry into an auction is strictly prohibited and
                    constitutes a material breach of these Terms.
                </p>

                <h3>4. STRUCTURE AND CONDUCT OF AUCTIONS</h3>
                <p>
                    4.1 Auctions may consist of multiple rounds, each governed by specific eligibility criteria,
                    timeframes, and service fee requirements.
                </p>
                <p>4.2 BCA retains sole and absolute discretion to determine:</p>
                <ul>
                    <li>(a) whether the minimum reserve price or vendor threshold has been met.</li>
                    <li>(b) whether the auction should proceed to a Best and Final Offer Stage.</li>
                    <li>(c) whether a cash offer takes precedence over a financed offer of equivalent nominal value.</li>
                    <li>(d) the format and timing of all bidding rounds.</li>
                </ul>
                <p>
                    4.3 Bidders who fail to participate within the applicable round timeframe forfeit the opportunity
                    to continue in that event. No retrospective participation, grace periods, or re-entries shall be
                    granted.
                </p>
                <p>
                    4.4 Where BCA determines that no qualifying offers have been received by the close of bidding, the
                    auction may be declared void or extended at BCA’s discretion. You accept that the auction outcome
                    is not guaranteed, and the seller is not obligated to accept any offer.
                </p>

                <h3>5. SERVICE FEES AND ACCESS RIGHTS</h3>
                <p>
                    5.1 Payment of the applicable Service Fee is a prerequisite for access to a given auction round.
                    This fee is non-refundable and payable per event or per round, as defined by BCA in each case.
                </p>
                <p>
                    5.2 The Service Fee is charged solely for access to the Platform. It is not a deposit, partial
                    payment, or financial consideration for the item listed in the auction, unless you have won the
                    auction.
                </p>
                <p>
                    5.3 Once payment is processed and Platform access is granted, the service shall be deemed fully
                    delivered and you expressly waive any statutory right to cancellation or refund to the maximum
                    extent permitted by law.
                </p>
                <p>
                    5.4 Payment disputes, chargebacks, or reversals will result in immediate suspension of access and
                    permanent disqualification from future auctions. BCA reserves the right to pursue recovery of any
                    fees, costs, or damages incurred as a result.
                </p>

                <h3>6. RESPONSIBILITIES OF BIDDERS</h3>
                <p>
                    6.1 You are solely responsible for determining the value of any asset listed, conducting
                    appropriate due diligence, and assessing your own legal or financial obligations before placing a
                    bid.
                </p>
                <p>
                    6.2 You accept that bidding is speculative and involves risk. BCA is not liable for any loss
                    arising from market fluctuations, misinterpretation of auction terms, or adverse change in asset
                    value post-bid.
                </p>
                <p>
                    6.3 If you are declared the Successful Bidder, you undertake to complete the transaction in full,
                    including payment of any required deposit, settlement via BCA’s designated legal channels, and
                    timely execution of relevant contracts.
                </p>
                <p>
                    6.4 Failure to complete a successful transaction in good faith may result in legal action,
                    forfeiture of any paid sums, and reporting to credit reference or enforcement bodies.
                </p>

                <h3>7. PLATFORM RULES AND SECURITY</h3>
                <p>
                    7.1 You must not interfere with the integrity or performance of the Platform, nor use any
                    software, script, crawler, or tool to manipulate bidding outcomes or access restricted content.
                </p>
                <p>
                    7.2 BCA uses commercially reasonable measures to secure the Platform and protect user data but does
                    not warrant that the Platform will be secure, uninterrupted, or free of viruses or technical
                    errors.
                </p>
                <p>
                    7.3 You are responsible for maintaining the confidentiality of your login credentials. Any action
                    taken under your account will be deemed authorized unless and until you notify BCA of a suspected
                    breach.
                </p>

                <h3>8. INTELLECTUAL PROPERTY</h3>
                <p>
                    8.1 All intellectual property rights in the Platform, including its structure, user interface,
                    logos, underlying code, branding, and proprietary algorithms, are and shall remain the exclusive
                    property of BCA or its licensors.
                </p>
                <p>
                    8.2 You agree not to reproduce, modify, distribute, or create derivative works based on any part of
                    the Platform without prior written authorization.
                </p>

                <h3>9. LIMITATION OF LIABILITY</h3>
                <p>
                    9.1 To the fullest extent permitted by law, BCA disclaims all liability for indirect,
                    consequential, punitive, or special damages arising from your use of the Platform or
                    participation in any auction.
                </p>
                <p>
                    9.2 BCA’s aggregate liability to you in respect of any single event or series of events shall not
                    exceed the total Service Fee paid by you in connection with the relevant auction.
                </p>
                <p>
                    9.3 Nothing in these Terms shall limit liability for death or personal injury caused by
                    negligence, fraud, or any other liability which cannot lawfully be excluded.
                </p>

                <h3>10. INDEMNITY</h3>
                <p>
                    You agree to indemnify, defend and hold harmless BCA, its officers, employees, agents, and
                    affiliates from and against all liabilities, costs, expenses, damages, and losses (including
                    legal fees) arising out of or in connection with your breach of these Terms, misuse of the
                    Platform, or infringement of third-party rights.
                </p>

                <h3>11. AMENDMENTS TO TERMS</h3>
                <p>
                    BCA reserves the right to update, vary or amend these Terms at any time. Changes shall take effect
                    upon publication on the Platform or upon written notice to you. Your continued use of the
                    Platform following such changes shall constitute your acceptance of the revised Terms.
                </p>

                <h3>12. GOVERNING LAW AND JURISDICTION</h3>
                <p>
                    These Terms and any dispute or claim (including non-contractual disputes or claims) arising from
                    or in connection with them or their subject matter shall be governed by and construed in
                    accordance with the laws of the State of Michigan. The courts of Oakland County have exclusive
                    jurisdiction to settle any such dispute or claim.
                </p>

                <h3>13. CONTACT</h3>
                <p>If you have any questions regarding these Terms or require legal or procedural clarification, you may contact:</p>
                <p>
                    <strong>Big Circle Auctions LLC</strong><br />
                    Email: contact@bigcircleauctions.com<br />
                    Address: 414 S Main Street #201, Rochester, MI 48307
                </p>

                <h3>SMS Terms</h3>
                <p>
                    Big Circle Auctions sends text messages to those that opt in. Messages we send include Customer
                    Care, Account Notification, and Marketing. Message frequency will vary based on the customer's
                    interaction and preferences. Message and data rates may apply. Text HELP or contact us at
                    contact@bigcircleauctions.com for support. Text STOP to opt out of future messages at any time.
                </p>
            </section>
        </div>
    );
}

export default Terms;
